import React, { useEffect } from "react";
import { Avatar, Button, Col, Form, Input, Modal, Popconfirm, Row, Select, Spin } from "antd";
import { EditOutlined, FlagOutlined, FileTextOutlined, SaveOutlined, UserOutlined, DisconnectOutlined } from "@ant-design/icons";
import { TacheService } from "../../services/TacheService";
import { PrioriteService } from "../../services/PrioriteService";
import Toast from "../../helpers/Toast";
import { catchError } from "../../services/DaoService";
import PropTypes from "prop-types";
import CustomFormItem from "../utils/form/CustomFormItem";
import { AiNameTrigger, AiNamePanel } from "../ai/AiNameSuggest";
import { useAiNameAssist } from "../../hooks/useAiNameAssist";
import { AffaireService } from "../../services/AffaireService";
import { PersonnelService } from "../../services/PersonnelService";
import { tachesState } from "../../state/actions/tachesState";
import style from "./TacheEditForm.module.scss";

const TacheEditForm = ({ visible, uid, onUpdate, onCancel }) => {
    const [form] = Form.useForm();
    const nomValue = Form.useWatch('nom', form);
    const nameAi = useAiNameAssist('tache');
    const [editing, setEditing] = React.useState(false);
    const [validationErrors, setValidationErrors] = React.useState({});
    const [loading, setLoading] = React.useState(true);

    const [priorites, setPriorites] = React.useState([]);
    const [personnels, setPersonnels] = React.useState([]);
    const [affaires, setAffaires] = React.useState([]);
    // Capturé une seule fois à l'ouverture : permet de griser le champ
    // "Dossier" si la tâche était déjà rattachée avant toute modification.
    const [initialAffaireId, setInitialAffaireId] = React.useState(null);
    const [detaching, setDetaching] = React.useState(false);

    useEffect(() => {
        if (!visible) return;

        setLoading(true);
        Promise.all([
            PrioriteService.getInstance().all(),
            PersonnelService.getInstance().all(),
            AffaireService.getInstance().all(),
        ])
            .then(([prioritesResponse, personnelsResponse, affairesResponse]) => {
                setPriorites(prioritesResponse.data);
                setPersonnels(personnelsResponse.data);
                setAffaires(affairesResponse.data);
            })
            .catch(catchError)
            .finally(() => setLoading(false));
    }, [visible]);

    useEffect(() => {
        if (uid) {
            setEditing(true);
            TacheService.getInstance()
                .find(uid)
                .then((response) => {
                    form.setFieldsValue(response.data);
                    setInitialAffaireId(response.data.affaire_id ?? null);
                })
                .catch(catchError)
                .finally(() => setEditing(false));
        }
    }, [uid, form]);

    const handleDetach = () => {
        setDetaching(true);
        TacheService.getInstance()
            .detach(uid)
            .then((response) => {
                Toast.success(response.message);
                tachesState.updateTacheInList(response.data);
                form.setFieldsValue(response.data);
                setInitialAffaireId(null);
                onUpdate(response.data);
            })
            .catch(catchError)
            .finally(() => setDetaching(false));
    };

    const handleSave = () => {
        setEditing(true);
        form
            .validateFields()
            .then((values) => {
                TacheService.getInstance()
                    .update(uid, values)
                    .then((response) => {
                        Toast.success(response.message);
                        tachesState.updateTacheInList(response.data);
                        onUpdate(response.data);
                    })
                    .catch((error) => {
                        const errors = error.response?.data;
                        if (errors?.validation && Object.keys(errors.errors).length) {
                            setValidationErrors(errors.errors);
                        } else {
                            catchError(error);
                        }
                    })
                    .finally(() => setEditing(false));
            })
            .catch(() => setEditing(false));
    };

    const TitleNode = (
        <div className={style.modalTitle}>
            <span className={style.titleIcon}>
                <EditOutlined />
            </span>
            <div>
                <span className={style.titleText}>Modifier la tâche</span>
                <span className={style.titleSubtitle}>
                    Mettez à jour les informations de cette tâche.
                </span>
            </div>
        </div>
    );

    const FooterNode = (
        <div className={style.modalFooter}>
            <Button className={style.cancelBtn} onClick={onCancel}>
                Annuler
            </Button>
            <Button
                type="primary"
                className={style.submitBtn}
                icon={<SaveOutlined />}
                loading={editing}
                onClick={handleSave}
            >
                Enregistrer les modifications
            </Button>
        </div>
    );

    return (
        <Modal
            width={680}
            centered
            className={style.tacheEditModal}
            wrapClassName={style.tacheEditModalWrap}
            destroyOnHidden
            open={visible}
            onCancel={onCancel}
            title={TitleNode}
            footer={FooterNode}
        >
            <Spin spinning={editing}>
                <Form layout="vertical" form={form} name="editTacheForm" className={style.form}>
                    {/* Section: Identification */}
                    <div className={style.sectionHeader}>
                        <span className={style.sectionIcon}>
                            <FileTextOutlined />
                        </span>
                        <div>
                            <p className={style.sectionTitle}>Identification</p>
                            <p className={style.sectionHint}>Nom de la tâche</p>
                        </div>
                    </div>

                    <Row gutter={16} className={style.formGrid}>
                        <Col xs={24}>
                            <CustomFormItem
                                label="Nom"
                                name="nom"
                                rules={[{ required: true, message: "Veuillez saisir le nom!" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Input
                                    className={style.control}
                                    placeholder="Intitulé de la tâche"
                                    suffix={
                                        <AiNameTrigger
                                            loading={nameAi.loading}
                                            disabled={!nomValue || nomValue.trim().length < 3}
                                            onClick={() => nameAi.run(form.getFieldValue('nom'))}
                                        />
                                    }
                                />
                            </CustomFormItem>
                            <AiNamePanel
                                status={nameAi.status}
                                proposal={nameAi.proposal}
                                onApply={(text) => { form.setFieldsValue({ nom: text }); nameAi.reset(); }}
                                onReset={nameAi.reset}
                            />
                        </Col>
                    </Row>

                    {/* Section: Planification */}
                    <div className={style.sectionHeader}>
                        <span className={style.sectionIcon}>
                            <FlagOutlined />
                        </span>
                        <div>
                            <p className={style.sectionTitle}>Planification</p>
                            <p className={style.sectionHint}>Dossier, priorité, responsable et échéance</p>
                        </div>
                    </div>

                    <Row gutter={16} className={style.formGrid}>
                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Dossier"
                                name="affaire_id"
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Select
                                    className={style.control}
                                    optionFilterProp="label"
                                    placeholder="Rattacher à un dossier"
                                    showSearch
                                    loading={loading}
                                    disabled={!!initialAffaireId}
                                    options={affaires.map((a) => ({ label: a.nom, value: a.id }))}
                                />
                            </CustomFormItem>
                            {!!initialAffaireId && (
                                <p style={{ fontSize: 12, color: 'var(--text-disabled, #8C9BAB)', margin: '-4px 0 6px' }}>
                                    Déjà rattachée à un dossier — détachez-la pour en choisir un autre.
                                </p>
                            )}
                            {!!initialAffaireId && (
                                <Popconfirm
                                    title="Détacher cette tâche de son dossier ?"
                                    description="Son code sera régénéré comme une tâche indépendante."
                                    okText="Détacher"
                                    cancelText="Annuler"
                                    onConfirm={handleDetach}
                                >
                                    <Button
                                        type="link"
                                        size="small"
                                        danger
                                        icon={<DisconnectOutlined />}
                                        loading={detaching}
                                        style={{ padding: 0 }}
                                    >
                                        Détacher du dossier
                                    </Button>
                                </Popconfirm>
                            )}
                        </Col>

                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Priorité"
                                name="priorite_id"
                                rules={[{ required: true, message: "Veuillez sélectionner la priorité!" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Select
                                    className={style.control}
                                    placeholder="Sélectionner la priorité"
                                    optionFilterProp="label"
                                    showSearch
                                    loading={loading}
                                    options={priorites.map((p) => ({
                                        label: (
                                            <span className={style.optionInline}>
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        background: p.couleur,
                                                        display: "inline-block",
                                                    }}
                                                />
                                                {p.nom}
                                            </span>
                                        ),
                                        value: p.id,
                                    }))}
                                />
                            </CustomFormItem>
                        </Col>

                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Responsable"
                                name="responsable_id"
                                rules={[{ required: true, message: "Veuillez définir un responsable" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Select
                                    className={style.control}
                                    optionFilterProp="full_name"
                                    placeholder="Sélectionner le responsable"
                                    showSearch
                                    loading={loading}
                                    options={personnels.map((personnel) => ({
                                        label: (
                                            <span className={style.userOption}>
                                                <Avatar
                                                    src={personnel.avatar_link}
                                                    alt={personnel.name}
                                                    size={22}
                                                    icon={<UserOutlined />}
                                                />
                                                <span>{personnel.name}</span>
                                            </span>
                                        ),
                                        value: personnel.id,
                                        full_name: personnel.name,
                                    }))}
                                />
                            </CustomFormItem>
                        </Col>

                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Date limite"
                                name="date_limite"
                                rules={[{ required: true, message: "Veuillez saisir la date limite!" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Input type="date" className={style.control} />
                            </CustomFormItem>
                        </Col>
                    </Row>

                    {/* Section: Description */}
                    <div className={style.sectionHeader}>
                        <span className={style.sectionIcon}>
                            <FileTextOutlined />
                        </span>
                        <div>
                            <p className={style.sectionTitle}>Description</p>
                            <p className={style.sectionHint}>Détails et contexte de la tâche</p>
                        </div>
                    </div>

                    <Row gutter={16} className={style.formGrid}>
                        <Col xs={24}>
                            <CustomFormItem
                                label="Description"
                                name="description"
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Input.TextArea
                                    rows={4}
                                    className={style.textarea}
                                    placeholder="Décrivez l'objectif ou le contexte de cette tâche…"
                                />
                            </CustomFormItem>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    );
};

export default TacheEditForm;

TacheEditForm.propTypes = {
    visible: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    uid: PropTypes.string.isRequired,
};
